import { InspectionDTO } from 'api';
import { formatDateTime } from 'date';
import { translate } from 'i18n';
import type { DataGridColumn } from 'ui';

import { RISK_SCORE_OPTIONS, RiskScoreChip } from '../../../risk';
import { INSPECTION_STATUSES } from '../../components/InspectionMutate/options';
import { InspectionStatusChip } from '../../components/InspectionStatusChip/InspectionStatusChip';

const EMPTY_VALUE = translate('backlog.inspectionsList.values.notAvailable', { namespace: 'features' });

export const columns: DataGridColumn<InspectionDTO>[] = [
  {
    columnSize: 'small',
    field: 'deal_id',
    filterable: true,
    headerName: translate('backlog.inspectionsList.columns.deal', { namespace: 'features' }),
    sortable: true,
    type: 'string',
    valueGetter: (_value, row) => {
      if (!row?.deal_id) {
        return EMPTY_VALUE;
      }
      return row?.deal_id;
    },
  },
  {
    columnSize: 'large',
    field: 'time',
    filterable: false,
    headerName: translate('backlog.inspectionsList.columns.time', { namespace: 'features' }),
    renderCell: (params) => {
      if (!params.value) {
        return EMPTY_VALUE;
      }
      return formatDateTime(params.value);
    },
    sortable: true,
  },
  {
    columnSize: 'medium',
    field: 'slug',
    filterable: true,
    headerName: translate('backlog.inspectionsList.columns.slug', { namespace: 'features' }),
    sortable: true,
    type: 'string',
  },
  {
    columnSize: 'medium',
    field: 'status',
    filterable: true,
    headerName: translate('backlog.inspectionsList.columns.status', { namespace: 'features' }),
    renderCell: (params) => {
      return <InspectionStatusChip status={params.value} />;
    },
    sortable: true,
    type: 'singleSelect',
    valueOptions: INSPECTION_STATUSES,
  },
  {
    columnSize: 'small',
    field: 'loads_count',
    filterable: true,
    headerName: translate('backlog.inspectionsList.columns.loads', { namespace: 'features' }),
    sortable: true,
    type: 'string',
  },
  {
    columnSize: 'large',
    field: 'producer',
    filterable: true,
    headerName: translate('backlog.inspectionsList.columns.producer', { namespace: 'features' }),
    sortable: true,
    type: 'string',
    valueGetter: (_value, row) => {
      if (!row?.producer) {
        return EMPTY_VALUE;
      }
      return row?.producer;
    },
  },
  {
    columnSize: 'large',
    field: 'end_processor',
    filterable: true,
    headerName: translate('backlog.inspectionsList.columns.endProcessor', { namespace: 'features' }),
    sortable: true,
    type: 'string',
    valueGetter: (_value, row) => {
      if (!row?.end_processor) {
        return EMPTY_VALUE;
      }
      return row?.end_processor;
    },
  },
  {
    columnSize: 'small',
    field: 'material',
    filterable: true,
    headerName: translate('backlog.inspectionsList.columns.material', { namespace: 'features' }),
    sortable: true,
    type: 'string',
    valueGetter: (_value, row) => {
      if (!row?.material) {
        return EMPTY_VALUE;
      }
      return row?.material;
    },
  },
  {
    columnSize: 'medium',
    field: 'risk_score',
    filterable: true,
    headerName: translate('backlog.inspectionsList.columns.risk', { namespace: 'features' }),
    renderCell: (params) => {
      if (!params.row?.risk_score) {
        return EMPTY_VALUE;
      }
      return <RiskScoreChip riskScore={params.row?.risk_score} />;
    },
    sortable: false,
    type: 'singleSelect',
    valueOptions: RISK_SCORE_OPTIONS,
  },
  {
    columnSize: 'medium',
    field: 'inspection_company',
    filterable: true,
    headerName: translate('backlog.inspectionsList.columns.company', { namespace: 'features' }),
    sortable: false,
    type: 'string',
    valueGetter: (_value, row) => {
      if (!row.inspection_company) {
        return EMPTY_VALUE;
      }
      return String(row.inspection_company.name);
    },
  },
  {
    columnSize: 'medium',
    field: 'inspector',
    filterable: true,
    headerName: translate('backlog.inspectionsList.columns.inspector', { namespace: 'features' }),
    sortable: false,
    type: 'string',
    valueGetter: (_value, row) => {
      if (!row.inspector) {
        return EMPTY_VALUE;
      }

      return String(row.inspector?.name);
    },
  },
];
